import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  ArrowsClockwise, CurrencyCircleDollar, ArrowUp, ArrowDown, CaretDown, CaretRight,
  Copy, ArrowSquareOut, Coins, LinkSimple, CheckCircle
} from "@phosphor-icons/react";
import { toast } from "sonner";

function fmt(val) {
  if (!val || val === 0) return "$0";
  if (val >= 1e12) return `$${(val / 1e12).toFixed(2)}T`;
  if (val >= 1e9) return `$${(val / 1e9).toFixed(2)}B`;
  if (val >= 1e6) return `$${(val / 1e6).toFixed(1)}M`;
  if (val >= 1e3) return `$${(val / 1e3).toFixed(0)}K`;
  return `$${val.toFixed(0)}`;
}

function Change({ val }) {
  if (val === 0) return <span className="text-[10px] text-zinc-600 font-data">0.00%</span>;
  return (
    <span className={`text-[10px] font-data inline-flex items-center gap-0.5 ${val > 0 ? "text-emerald-400" : "text-red-400"}`}>
      {val > 0 ? <ArrowUp size={8} weight="bold" /> : <ArrowDown size={8} weight="bold" />}
      {val > 0 ? "+" : ""}{val.toFixed(2)}%
    </span>
  );
}

function ChainRow({ chain }) {
  const copyAddr = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(chain.contract_address);
    toast.success("Address copied");
  };
  return (
    <div className="flex items-center gap-3 py-1.5 border-b border-zinc-800/30 last:border-0 text-[10px]">
      <span className="text-zinc-300 w-24 flex-shrink-0 truncate">{chain.chain}</span>
      <span className="font-data text-zinc-200 w-20 text-right flex-shrink-0">{fmt(chain.mcap)}</span>
      {chain.contract_address ? (
        <div className="flex items-center gap-1.5 flex-1 min-w-0">
          <LinkSimple size={9} className="text-zinc-600 flex-shrink-0" />
          <span className="font-data text-zinc-500 truncate">
            {chain.contract_address.slice(0, 8)}...{chain.contract_address.slice(-4)}
          </span>
          <button onClick={copyAddr} className="p-0.5 hover:bg-zinc-800 rounded-sm flex-shrink-0" title="Copy">
            <Copy size={9} className="text-zinc-600 hover:text-zinc-300" />
          </button>
          {chain.explorer_url && (
            <a href={chain.explorer_url} target="_blank" rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
              className="text-[8px] text-zinc-500 hover:text-blue-400 flex items-center gap-0.5 flex-shrink-0"
            >
              <ArrowSquareOut size={8} />{chain.explorer_name || "Scan"}
            </a>
          )}
        </div>
      ) : (
        <span className="text-zinc-700 text-[9px] flex-1">—</span>
      )}
    </div>
  );
}

function StableRow({ stable }) {
  const [expanded, setExpanded] = useState(false);
  const rankColors = ["text-amber-400", "text-zinc-300", "text-amber-700"];
  const mechColors = {
    "fiat-backed": "bg-blue-500/10 text-blue-400",
    "crypto-backed": "bg-violet-500/10 text-violet-400",
    "algorithmic": "bg-amber-500/10 text-amber-400",
  };

  return (
    <div data-testid={`stable-row-${stable.rank}`} className="border-b border-zinc-800/40 last:border-0">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full grid grid-cols-[36px_1fr_110px_80px_80px_80px_60px_24px] items-center gap-2 px-4 py-3 hover:bg-zinc-900/40 transition-colors text-left"
        data-testid={`stable-toggle-${stable.rank}`}
      >
        <span className={`text-sm font-data font-bold text-center ${stable.rank <= 3 ? rankColors[stable.rank - 1] : "text-zinc-600"}`}>
          {stable.rank}
        </span>

        <div className="flex items-center gap-2.5 min-w-0">
          {stable.logo ? (
            <img src={stable.logo} alt={stable.symbol} className="w-7 h-7 rounded-full flex-shrink-0" loading="lazy" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
          ) : (
            <div className="w-7 h-7 rounded-full bg-zinc-800 flex items-center justify-center flex-shrink-0">
              <span className="text-[9px] font-data font-bold text-zinc-300">{stable.symbol.slice(0, 2)}</span>
            </div>
          )}
          <div className="min-w-0">
            <p className="text-xs font-medium text-zinc-100 truncate">{stable.name}</p>
            <div className="flex items-center gap-1.5">
              <span className="text-[9px] text-zinc-500 font-data">{stable.symbol}</span>
              <Badge className={`text-[7px] px-1 py-0 border-0 rounded-sm h-3.5 ${mechColors[stable.peg_mechanism] || "bg-zinc-800 text-zinc-500"}`}>
                {stable.peg_mechanism}
              </Badge>
            </div>
          </div>
        </div>

        <div className="text-right">
          <p className="text-xs font-data font-medium text-zinc-100">{fmt(stable.mcap)}</p>
          <p className="text-[9px] text-zinc-600">{stable.dominance}% dom</p>
        </div>

        <div className="text-right"><Change val={stable.change_1d} /></div>
        <div className="text-right"><Change val={stable.change_7d} /></div>
        <div className="text-right"><Change val={stable.change_30d} /></div>

        <div className="text-right">
          <span className="text-[10px] font-data text-zinc-400">{stable.chain_count}</span>
        </div>

        <div className="flex justify-center">
          {expanded ? <CaretDown size={12} className="text-zinc-500" /> : <CaretRight size={12} className="text-zinc-500" />}
        </div>
      </button>

      {expanded && stable.chains.length > 0 && (
        <div className="px-4 pb-3 pt-0 ml-10 mr-4" data-testid={`stable-chains-${stable.rank}`}>
          <div className="bg-zinc-950/50 border border-zinc-800/40 rounded-sm p-3">
            <p className="text-[9px] uppercase tracking-wider text-zinc-600 mb-2 font-medium">Chain Distribution & Contracts</p>
            {stable.chains.map((c, i) => <ChainRow key={`${c.chain}-${i}`} chain={c} />)}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Stablecoins() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchStables = async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const resp = await axios.get(`${API}/stablecoins`);
      setData(resp.data);
    } catch (e) {
      console.error("Failed to fetch stablecoins:", e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { fetchStables(); }, []);

  const stables = data?.stablecoins?.slice(0, 10) || [];

  return (
    <div className="p-5 max-w-[1200px]" data-testid="stablecoins-page">
      {/* Header */}
      <div className="flex items-start justify-between mb-5">
        <div>
          <div className="flex items-center gap-2.5 mb-1">
            <Coins size={22} weight="bold" className="text-emerald-400" />
            <h1 className="font-heading text-2xl font-bold text-zinc-50 tracking-tight">Top Stablecoins</h1>
          </div>
          <p className="text-xs text-zinc-500 ml-[34px]">
            Live market cap, chain distribution & contract addresses from DeFiLlama
          </p>
        </div>
        <button
          data-testid="refresh-stables-btn"
          onClick={() => fetchStables(true)}
          disabled={refreshing}
          className="flex items-center gap-1.5 text-[10px] text-zinc-400 hover:text-zinc-200 transition-colors px-2.5 py-1.5 border border-zinc-800 rounded-sm"
        >
          <ArrowsClockwise size={12} className={refreshing ? "animate-spin" : ""} />
          {refreshing ? "Updating..." : "Refresh"}
        </button>
      </div>

      {/* Summary */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5" data-testid="stables-summary">
          <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-sm p-3.5">
            <div className="flex items-center gap-2 mb-1.5">
              <CurrencyCircleDollar size={14} className="text-emerald-400" />
              <span className="text-[9px] uppercase tracking-wider text-zinc-500">Total Stablecoin MCap</span>
            </div>
            <p className="text-lg font-data font-bold text-zinc-100">{fmt(data.total_mcap)}</p>
          </div>
          {stables[0] && (
            <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-sm p-3.5">
              <div className="flex items-center gap-2 mb-1.5">
                <CheckCircle size={14} weight="fill" className="text-amber-400" />
                <span className="text-[9px] uppercase tracking-wider text-zinc-500">#1 Stablecoin</span>
              </div>
              <p className="text-lg font-data font-bold text-zinc-100">{stables[0].symbol}</p>
              <p className="text-[10px] text-zinc-500">{stables[0].dominance}% dominance</p>
            </div>
          )}
          {stables[1] && (
            <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-sm p-3.5">
              <div className="flex items-center gap-2 mb-1.5">
                <CheckCircle size={14} className="text-zinc-500" />
                <span className="text-[9px] uppercase tracking-wider text-zinc-500">#2 Stablecoin</span>
              </div>
              <p className="text-lg font-data font-bold text-zinc-100">{stables[1].symbol}</p>
              <p className="text-[10px] text-zinc-500">{stables[1].dominance}% dominance</p>
            </div>
          )}
          <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-sm p-3.5">
            <div className="flex items-center gap-2 mb-1.5">
              <Coins size={14} className="text-violet-400" />
              <span className="text-[9px] uppercase tracking-wider text-zinc-500">Tracked</span>
            </div>
            <p className="text-lg font-data font-bold text-zinc-100">Top 10</p>
            <p className="text-[10px] text-zinc-500">Click to expand chains</p>
          </div>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="space-y-2">
          {[...Array(10)].map((_, i) => <Skeleton key={i} className="h-14 bg-zinc-900/50" />)}
        </div>
      )}

      {/* Table */}
      {!loading && stables.length > 0 && (
        <div className="bg-zinc-900/30 border border-zinc-800/60 rounded-sm overflow-hidden" data-testid="stables-table">
          {/* Header */}
          <div className="grid grid-cols-[36px_1fr_110px_80px_80px_80px_60px_24px] items-center gap-2 px-4 py-2.5 border-b border-zinc-800/60 bg-zinc-900/50">
            <span className="text-[9px] uppercase tracking-wider text-zinc-600 text-center">#</span>
            <span className="text-[9px] uppercase tracking-wider text-zinc-600">Stablecoin</span>
            <span className="text-[9px] uppercase tracking-wider text-zinc-600 text-right">Market Cap</span>
            <span className="text-[9px] uppercase tracking-wider text-zinc-600 text-right">1D</span>
            <span className="text-[9px] uppercase tracking-wider text-zinc-600 text-right">7D</span>
            <span className="text-[9px] uppercase tracking-wider text-zinc-600 text-right">30D</span>
            <span className="text-[9px] uppercase tracking-wider text-zinc-600 text-right">Chains</span>
            <span />
          </div>

          {stables.map(s => <StableRow key={s.rank} stable={s} />)}
        </div>
      )}

      {/* Footer */}
      {data && (
        <div className="mt-4 pt-3 border-t border-zinc-800/40 flex items-center justify-between">
          <p className="text-[10px] text-zinc-600">Source: DeFiLlama (defillama.com)</p>
          <p className="text-[10px] text-zinc-600">Updated: {new Date(data.fetched_at).toLocaleString()}</p>
        </div>
      )}
    </div>
  );
}
